import React, { useCallback, useEffect, useState } from 'react';
import {
  LockClosedIcon,
  EnvelopeIcon,
  Squares2X2Icon,
  SparklesIcon,
  PhotoIcon,
  VideoCameraIcon,
  ChatBubbleLeftIcon,
  DevicePhoneMobileIcon,
  ArrowRightOnRectangleIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowPathIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { CaptchaChallengeResponse, LoginRequest } from '@packages/global';
import { authApi } from '../services/api';
import { useToast } from '../stores/toastStore';
import { useTenantStore } from '../stores/tenantStore';
import { useTranslation } from 'react-i18next';
import ThemeToggle from '../components/ThemeToggle';
import LanguageSwitcher from '../components/LanguageSwitcher';

function getAuthErrorMessage(error: any, fallback: string): string {
  const message = error.response?.data?.message ?? error.response?.data?.error;
  if (Array.isArray(message)) {
    return message[0] || fallback;
  }
  return typeof message === 'string' && message ? message : fallback;
}

const LoginPage: React.FC = () => {
  const { t } = useTranslation(['auth', 'app', 'common']);
  const [loading, setLoading] = useState(false);
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [captcha, setCaptcha] = useState<CaptchaChallengeResponse | null>(null);
  const [formData, setFormData] = useState<LoginRequest>({
    email: '',
    password: '',
    captchaId: '',
    captchaCode: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof LoginRequest, string>>>({});
  const { login } = useAuthStore();
  const navigate = useNavigate();
  const { info } = useToast();
  const { name: tenantName, slogan: tenantSlogan } = useTenantStore();

  const displayName = tenantName || '';
  const displaySlogan = tenantSlogan || '';

  const loadCaptcha = useCallback(async () => {
    setCaptchaLoading(true);
    try {
      const nextCaptcha = await authApi.getCaptcha();
      setCaptcha(nextCaptcha);
      setFormData((current) => ({
        ...current,
        captchaId: nextCaptcha.captchaId,
        captchaCode: '',
      }));
      setErrors((current) => ({ ...current, captchaCode: undefined }));
    } catch {
      setCaptcha(null);
      setFormData((current) => ({ ...current, captchaId: '', captchaCode: '' }));
      setErrors((current) => ({ ...current, captchaCode: t('auth:captchaLoadFailed') }));
    } finally {
      setCaptchaLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadCaptcha();
  }, [loadCaptcha]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof LoginRequest, string>> = {};

    if (!formData.email) {
      newErrors.email = t('auth:emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('auth:emailInvalid');
    }

    if (!formData.password) {
      newErrors.password = t('auth:passwordRequired');
    }

    if (!formData.captchaId || !formData.captchaCode.trim()) {
      newErrors.captchaCode = t('auth:captchaRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await login(formData);
      navigate('/dashboard');
    } catch (error: any) {
      const message = getAuthErrorMessage(error, t('auth:loginFailed'));
      setErrors(message.includes('验证码') || message.toLowerCase().includes('captcha')
        ? { captchaCode: message }
        : { email: message });
      void loadCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof LoginRequest) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({ ...formData, [field]: e.target.value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: undefined });
    }
  };

  const handleComingSoon = () => {
    info(t('app:comingSoon'));
  };

  return (
    <div className="h-screen bg-bg-page overflow-hidden flex">
      {/* 左侧品牌区域 */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary via-primary-light to-primary-lighter relative overflow-hidden">
        {/* 背景装饰 */}
        <div className="absolute inset-0 opacity-20">
          <div className="w-full h-full bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.15)_1px,transparent_1px)] bg-[length:40px_40px]"></div>
        </div>
        <div className="absolute -top-20 -left-20 w-72 h-72 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-white/5 rounded-full blur-2xl"></div>

        {/* 内容区域 */}
        <div className="relative flex flex-col justify-between w-full p-12">
          {/* 顶部 Logo */}
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-xl flex items-center justify-center">
              <Squares2X2Icon className="w-6 h-6 text-white" />
            </div>
            <div className="flex flex-col">
              <span className="text-white font-semibold text-lg tracking-wide font-serif">
                {displayName}
              </span>
              <span className="text-white/60 text-xs">{displaySlogan}</span>
            </div>
          </div>

          {/* 中间主标题 */}
          <div className="flex-1 flex flex-col justify-center">
            <h1 className="text-6xl font-bold text-white leading-tight mb-6">
              {t('auth:heroTitle1')}<br />{t('auth:heroTitle2')}
            </h1>
            <p className="text-white/80 text-base leading-relaxed max-w-md">
              {t('auth:heroSubtitle1')}<br />
              {t('auth:heroSubtitle2')}
            </p>
          </div>

          {/* 底部功能卡片 */}
          <div className="space-y-3">
            <div className="flex items-center gap-4 py-2">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <SparklesIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white text-[15px]">{t('auth:featureScript')}</h3>
                <p className="text-sm text-white/60">{t('auth:featureScriptDesc')}</p>
              </div>
            </div>

            <div className="flex items-center gap-4 py-2">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <PhotoIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white text-[15px]">{t('auth:featureImage')}</h3>
                <p className="text-sm text-white/60">{t('auth:featureImageDesc')}</p>
              </div>
            </div>

            <div className="flex items-center gap-4 py-2">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <VideoCameraIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white text-[15px]">{t('auth:featureVideo')}</h3>
                <p className="text-sm text-white/60">{t('auth:featureVideoDesc')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 右侧登录表单 */}
      <div className="w-full lg:w-1/2 bg-bg-surface flex items-center justify-center relative">
        {/* 右上角：主题 & 语言切换图标 */}
        <div className="absolute top-4 right-4 z-20 flex items-center gap-1">
          <ThemeToggle compact />
          <LanguageSwitcher compact />
        </div>

        {/* 装饰光晕 */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-primary/3 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>

        <div className="w-full max-w-md px-8 lg:px-12 relative z-10">
          {/* 移动端 Logo */}
          <div className="lg:hidden text-center mb-10">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary to-primary-lighter rounded-2xl mb-4 shadow-lg">
              <Squares2X2Icon className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-50 mb-1 font-serif">
              {displayName}
            </h1>
            <p className="text-gray-500 text-sm">{displaySlogan}</p>
          </div>

          {/* 表单标题 */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-50 mb-3">
              {t('auth:welcomeBack')}
            </h2>
            <p className="text-gray-500">{t('auth:loginJourney')}</p>
          </div>

          {/* 登录表单 */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label
                htmlFor="email"
                className="block text-[13px] font-medium text-gray-400 mb-2"
              >
                {t('auth:email')}
              </label>
              <div className="relative w-full">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <EnvelopeIcon className="h-[18px] w-[18px] text-gray-500" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange('email')}
                  className={`box-border w-full pl-11 pr-4 py-3 border ${errors.email ? 'border-error' : 'border-border-subtle'
                    } rounded-lg text-gray-50 placeholder-gray-500 bg-bg-form hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all duration-300`}
                  placeholder={t('auth:emailAddressPlaceholder')}
                />
              </div>
              {errors.email && (
                <p className="mt-2 text-sm text-error">{errors.email}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-[13px] font-medium text-gray-400 mb-2"
              >
                {t('auth:password')}
              </label>
              <div className="relative w-full">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <LockClosedIcon className="h-[18px] w-[18px] text-gray-500" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange('password')}
                  className={`box-border w-full pl-11 pr-11 py-3 border ${errors.password ? 'border-error' : 'border-border-subtle'
                    } rounded-lg text-gray-50 placeholder-gray-500 bg-bg-form hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all duration-300`}
                  placeholder={t('auth:passwordPlaceholder')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center cursor-pointer text-gray-500 hover:text-gray-400 transition-colors"
                  tabIndex={-1}
                >
                  {showPassword
                    ? <EyeSlashIcon className="h-[18px] w-[18px]" />
                    : <EyeIcon className="h-[18px] w-[18px]" />
                  }
                </button>
              </div>
              {errors.password && (
                <p className="mt-2 text-sm text-error">{errors.password}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="captchaCode"
                className="block text-[13px] font-medium text-gray-400 mb-2"
              >
                {t('auth:captcha')}
              </label>
              <div className="flex gap-3">
                <div className="relative min-w-0 flex-1">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <ShieldCheckIcon className="h-[18px] w-[18px] text-gray-500" />
                  </div>
                  <input
                    id="captchaCode"
                    name="captchaCode"
                    type="text"
                    autoComplete="off"
                    inputMode="text"
                    maxLength={8}
                    value={formData.captchaCode}
                    onChange={handleChange('captchaCode')}
                    className={`box-border w-full pl-11 pr-4 py-3 border ${errors.captchaCode ? 'border-error' : 'border-border-subtle'
                      } rounded-lg text-gray-50 placeholder-gray-500 bg-bg-form hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all duration-300`}
                    placeholder={t('auth:captchaPlaceholder')}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => void loadCaptcha()}
                  disabled={captchaLoading}
                  className="flex h-[50px] w-[136px] shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border-subtle bg-bg-form text-gray-400 transition-all duration-300 hover:border-primary/30 hover:bg-bg-elevated disabled:cursor-not-allowed disabled:opacity-70"
                  aria-label={t('auth:refreshCaptcha')}
                  title={t('auth:refreshCaptcha')}
                >
                  {captcha?.imageDataUrl ? (
                    <img
                      src={captcha.imageDataUrl}
                      alt={t('auth:captchaImageAlt')}
                      className="h-full w-full object-cover"
                      draggable={false}
                    />
                  ) : (
                    <ArrowPathIcon className={`h-5 w-5 ${captchaLoading ? 'animate-spin' : ''}`} />
                  )}
                </button>
              </div>
              {errors.captchaCode && (
                <p className="mt-2 text-sm text-error">{errors.captchaCode}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3 rounded-lg bg-gradient-to-r from-primary to-primary-lighter font-semibold text-base text-white flex items-center justify-center gap-2 shadow-[0_4px_20px_rgba(255,92,0,0.25)] transition-all duration-300 cursor-pointer ${loading
                ? 'opacity-70 cursor-not-allowed'
                : 'hover:shadow-[0_4px_25px_rgba(255,92,0,0.4)] active:scale-[0.98]'
                }`}
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5" />
              {loading ? t('auth:loggingIn') : t('auth:login')}
            </button>
          </form>

          {/* 分隔线 */}
          <div className="flex items-center gap-4 my-8">
            <div className="flex-1 h-px bg-border-subtle"></div>
            <span className="text-gray-500 text-xs">{t('auth:or')}</span>
            <div className="flex-1 h-px bg-border-subtle"></div>
          </div>

          {/* 社交登录 */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleComingSoon}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-bg-form border border-border-subtle rounded-lg text-gray-400 text-sm font-medium hover:border-primary/30 hover:bg-bg-elevated transition-all duration-300 cursor-pointer"
            >
              <ChatBubbleLeftIcon className="w-[18px] h-[18px] text-success" />
              {t('auth:wechatLogin')}
            </button>
            <button
              type="button"
              onClick={handleComingSoon}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-bg-form border border-border-subtle rounded-lg text-gray-400 text-sm font-medium hover:border-primary/30 hover:bg-bg-elevated transition-all duration-300 cursor-pointer"
            >
              <DevicePhoneMobileIcon className="w-[18px] h-[18px] text-info" />
              {t('auth:phoneLogin')}
            </button>
          </div>

          {/* 注册链接 */}
          <div className="text-center mt-8">
            <span className="text-gray-500 text-[13px]">{t('auth:noAccountYet')}</span>
            <button
              type="button"
              onClick={handleComingSoon}
              className="text-primary text-[13px] font-semibold ml-1 hover:text-primary-light transition-colors cursor-pointer"
            >
              {t('auth:registerNow')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
